/**
 * useWritableRegions — the regions this viewer may CREATE a booking in
 * (BON-142). Split out of `WorkshopPicker` in BON-146.
 *
 * Reads are nation-wide for every cross-region staff role, so the unfiltered
 * region tree is right for browsing but wrong for a write picker: a Regional
 * Director could select a workshop in another region and only discover the
 * refusal when they pressed submit. The server enforces the boundary either way
 * (a write needs region write-breadth), so this is about not OFFERING a choice
 * that cannot work — not about security.
 *
 * `breadth_write:all` — admins and CSRs — is unrestricted. Anyone else gets only
 * their assigned regions, and picking one expands to its subtree server-side, so
 * an RD assigned NYC still reaches Brooklyn.
 *
 * The REBOOK picker does not use this: its targets are server-filtered to the
 * caller's write breadth already, and confined to one region besides.
 */
import { useEffect, useMemo, useState } from 'react';

import { fetchRegions, type RegionNode } from '../../../api/regions';
import { useViewerRegions } from '../../../hooks/use-viewer-regions';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

/** Root-level region nodes, each carrying its full nested subtree — the shape
 *  `RegionTreeSelect` renders as a hierarchical dropdown. Picking any node
 *  (parent or descendant) expands to its subtree server-side, so the filter's
 *  own tree needn't be flattened client-side. */
function useRegionTree(): RegionNode[] {
  const [regions, setRegions] = useState<RegionNode[]>([]);
  useEffect(() => {
    fetchRegions()
      .then((tree) => setRegions(tree.filter((r) => r.parentId === null)))
      .catch(() => {});
  }, []);
  return regions;
}

export function useWritableRegions(): { regions: RegionNode[]; confined: boolean } {
  const all = useRegionTree();
  const { hasPermission } = usePermissions();
  const { regionIds } = useViewerRegions();

  // Confinement must be POSITIVELY established, never inferred from an absent
  // permission. `hasPermission` answers false while the token is still loading,
  // so testing for "not nation-wide" would briefly confine every viewer — and
  // confine them to an empty region list, hiding every workshop. Requiring the
  // assigned-breadth grant explicitly means the ambiguous states (loading, or a
  // viewer with neither grant) fall through to unrestricted, which is safe
  // because the server refuses the write regardless.
  const nationWide = hasPermission(Permission.BREADTH_WRITE_ALL);
  const confinedToOwn = !nationWide && hasPermission(Permission.BREADTH_WRITE_ASSIGNED);

  return useMemo(() => {
    if (!confinedToOwn) return { regions: all, confined: false };
    const mine = new Set(regionIds);
    return { regions: all.filter((r) => mine.has(r.id)), confined: true };
  }, [all, confinedToOwn, regionIds]);
}
